import { useMemo } from "react";
import { useTimelineContext } from "../../context/use-timeline-context";
import { useTimelineSize } from "../../context/use-timeline-size";
import { useTimelineZoom } from "../../context/use-timeline-zoom";
import { TicksBackgroundWrapper, TicksWrapper, TickWrapper } from "./styled";

export const TICKS_HEIGHT = 30;

type TimelineTickMark = {
  width: number;
  label: string;
};

const BASE_INTERVALS = [0.1, 0.5, 1, 5, 10, 30, 60, 300] as const;

const DESIRED_TIMELINE_DIVISIONS = 15;

const findBestTimeInterval = ({
  totalDurationInFrames,
  fps,
  zoom,
}: {
  totalDurationInFrames: number;
  fps: number;
  zoom: number;
}) => {
  const durationInSeconds = totalDurationInFrames / fps;
  const baseInterval = durationInSeconds / (DESIRED_TIMELINE_DIVISIONS * zoom);

  const interval =
    BASE_INTERVALS.find((int) => int >= baseInterval) ??
    BASE_INTERVALS[BASE_INTERVALS.length - 1];

  return interval;
};

const formatTimecode = (frame: number, fps: number, interval: number) => {
  const totalSeconds = frame / fps;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  if (hours > 0) {
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  const base = `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;

  if (interval === 0.5) {
    const milliseconds = Math.round((totalSeconds % 1) * 1000);
    const snappedMilliseconds = milliseconds >= 500 ? 500 : 0;
    return `${base}.${snappedMilliseconds.toString().padStart(3, "0")}`;
  }

  return base;
};

export const TicksBackground = () => (
  <TicksBackgroundWrapper style={{ height: TICKS_HEIGHT }} />
);

const Ticks = ({
  timelineHeight,
  visibleFrames,
}: {
  timelineHeight: number;
  visibleFrames: number;
}) => {
  const { fps } = useTimelineContext();
  const { zoom } = useTimelineZoom();
  const { timelineWidth } = useTimelineSize();
  const tickMarks = useMemo(() => {
    const interval = findBestTimeInterval({
      totalDurationInFrames: visibleFrames,
      fps,
      zoom,
    });

    const marks: TimelineTickMark[] = [];

    const pxPerSecond = timelineWidth / (visibleFrames / fps);
    const pixelsBetweenTicks = interval * pxPerSecond;

    for (
      let xPosition = pixelsBetweenTicks;
      xPosition <= timelineWidth;
      xPosition += pixelsBetweenTicks
    ) {
      const seconds = xPosition / pxPerSecond;
      const frame = Math.round(seconds * fps);
      marks.push({
        width: pixelsBetweenTicks,
        label: formatTimecode(frame, fps, interval),
      });
    }
    return marks;
  }, [fps, timelineWidth, visibleFrames, zoom]);

  return (
    <TicksWrapper id="ticks">
      {tickMarks.map((tick, i) => (
        <TickWrapper
          key={i}
          style={{
            fontSize: 10,
            width: tick.width,
            minWidth: tick.width,
            height: timelineHeight + TICKS_HEIGHT,
          }}
        >
          {tick.label}
        </TickWrapper>
      ))}
    </TicksWrapper>
  );
};

export default Ticks;
